import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { Memory, MemoryUpdate } from '../../hooks/use-memories'
import { Loader2 } from 'lucide-react'

interface MemoryEditModalProps {
  memory: Memory | null
  isOpen: boolean
  onClose: () => void
  onSave: (memoryId: string, updates: MemoryUpdate) => void
  isSaving?: boolean
}

export function MemoryEditModal({ memory, isOpen, onClose, onSave, isSaving }: MemoryEditModalProps) {
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [importance, setImportance] = useState<'high' | 'medium' | 'low'>('medium')
  const [memoryType, setMemoryType] = useState<'factual' | 'episodic' | 'semantic'>('factual')

  useEffect(() => {
    if (memory) {
      setContent(memory.content)
      setCategory(memory.category)
      setSubcategory(memory.subcategory)
      setImportance(memory.importance as 'high' | 'medium' | 'low')
      setMemoryType(memory.memory_type as 'factual' | 'episodic' | 'semantic')
    }
  }, [memory])

  const handleSave = () => {
    if (!memory) return

    const updates: MemoryUpdate = {}
    if (content !== memory.content) updates.content = content
    if (category !== memory.category) updates.category = category
    if (subcategory !== memory.subcategory) updates.subcategory = subcategory
    if (importance !== memory.importance) updates.importance = importance
    if (memoryType !== memory.memory_type) updates.memory_type = memoryType

    if (Object.keys(updates).length > 0) {
      onSave(memory.id, updates)
    } else {
      onClose()
    }
  }

  if (!memory) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit memory</DialogTitle>
          <DialogDescription>
            Update the memory content and metadata.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subcategory">Subcategory</Label>
              <Input
                id="subcategory"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="importance">Importance</Label>
              <Select value={importance} onValueChange={(value: 'high' | 'medium' | 'low') => setImportance(value)}>
                <SelectTrigger id="importance">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="memoryType">Memory type</Label>
              <Select value={memoryType} onValueChange={(value: 'factual' | 'episodic' | 'semantic') => setMemoryType(value)}>
                <SelectTrigger id="memoryType">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="factual">Factual</SelectItem>
                  <SelectItem value="episodic">Episodic</SelectItem>
                  <SelectItem value="semantic">Semantic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

